import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ChevronRight, Plus } from 'lucide-react';
import {
  CREDIT_REFRESH_EVENT,
  CreditBalance,
  fetchUserCreditBalance,
} from '../../services/api/creditService';
import { CREDIT_INSUFFICIENT_EVENT } from '../../services/api/axiosConfig';
import { RootState } from '../../redux/store';
import { selectIsUserLeader } from '../../redux/organization/organizationSlice';
import CreditPurchaseModal from './CreditPurchaseModal';
import CreditUsageModal from './CreditUsageModal';
import { usePolling } from '../../hooks/usePolling';

/* 旧 styled-components 定義をクラス文字列定数として保持。
 * 旧 `@keyframes fadeIn`（opacity + translateY(-4px)）は globals.css の
 * `msc-fade-in-4` へ移設。font-size は line-height の有無を見て arbitrary で保持。 */
const CONTAINER = 'relative';

const BADGE =
  'inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E2DFD6] ' +
  'rounded-[999px] text-[13px] font-medium text-gray-700 cursor-pointer ' +
  'transition-all duration-150 shadow-[0_0.5px_3px_0_rgba(0,0,0,0.06)] ' +
  'hover:bg-[#F9F8F6] hover:border-[#D1CFC7] [&_svg]:text-gray-500'; /* #374151 / #6B7280 */

const DROPDOWN =
  'absolute top-[calc(100%+8px)] right-0 w-[min(320px,calc(100vw-32px))] bg-white border border-[#E2DFD6] ' +
  'rounded-tasky-lg shadow-[0_8px_24px_rgba(0,0,0,0.12)] z-[100] ' +
  'animate-[msc-fade-in-4_0.15s_ease-out] overflow-hidden';

const DROPDOWN_HEADER = 'pt-4 px-4 pb-3 border-b border-b-[#E2DFD6] border-dashed';

/* `font-family: serif` は Tailwind の font-serif（ui-serif スタック）とは別物なので
 * arbitrary property で素の `serif` を保持する。 */
const ORG_NAME = 'text-[15px] font-bold text-gray-900 [font-family:serif]'; /* #111827 */

const DROPDOWN_BODY = 'px-4 py-3 flex flex-col gap-4';

const SECTION = 'flex flex-col gap-1';

const SECTION_HEADER = 'flex items-center gap-1.5';

const SECTION_ICON = 'w-5 h-5 flex items-center justify-center text-gray-500'; /* #6B7280 */

const SECTION_TITLE = 'text-[14px] font-medium text-gray-900 flex-1';

const SECTION_VALUE = 'text-[14px] font-medium text-gray-900';

const DETAIL_LIST = 'list-none m-0 p-0 pl-[26px] flex flex-col gap-0.5';

const DETAIL_ITEM = 'flex items-center justify-between text-[13px] leading-[18px] text-gray-500';

const DETAIL_VALUE = 'shrink-0';

const REFRESH_NOTE = 'text-[12px] leading-[18px] text-gray-500 m-0 pl-[26px]';

const PURCHASE_LINK =
  'flex items-center justify-between w-full px-4 py-3 bg-transparent ' +
  'border-t border-t-[#E2DFD6] text-[14px] font-medium text-indigo-500 cursor-pointer ' +
  'transition-[background] duration-150 hover:bg-[#F5F3FF] [&_svg]:text-indigo-500'; /* #6366F1 */

const PURCHASE_LINK_CONTENT = 'flex items-center gap-1.5';

const USAGE_LINK =
  'flex items-center justify-between w-full px-4 py-3 bg-transparent ' +
  'border-t border-t-[#E2DFD6] text-[14px] font-medium text-gray-900 cursor-pointer ' +
  'transition-[background] duration-150 hover:bg-[#F9F8F6] [&_svg]:text-gray-400'; /* #9CA3AF */

const CreditBadge: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const organizationId = user?.organization_id ?? user?.organization?.id ?? null;
  // クレジット購入は backend 側でもリーダー限定 (403)。auth.user.role が member の
  // まま昇格したケースを拾うため、3 経路チェックの selectIsUserLeader を使う。
  const isLeader = useSelector(selectIsUserLeader);

  const [creditData, setCreditData] = useState<CreditBalance | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const loadCredits = useCallback(async () => {
    try {
      const data = await fetchUserCreditBalance();
      setCreditData(data);
    } finally {
      setLoading(false);
    }
  }, []);

  // 非表示タブでは止め、表示に戻ったら取り直す。401/403 (トライアル切れ含む) で止まり、
  // 通信断が続くときは最大 5 分まで間隔を延ばす。
  const { run: refreshCredits } = usePolling(loadCredits, {
    enabled: !!organizationId,
    intervalMs: 60000,
    maxBackoffMs: 5 * 60000,
  });

  useEffect(() => {
    // 組織未所属ユーザー (オンボーディング未完了など) は credit 機能対象外。
    // organization_id が確定するまで API を呼ばずに badge も非表示にする。
    if (!organizationId) {
      setLoading(false);
      setCreditData(null);
      return;
    }
    void refreshCredits();
    const handleCreditRefresh = () => {
      void refreshCredits();
    };
    window.addEventListener(CREDIT_REFRESH_EVENT, handleCreditRefresh);

    return () => {
      window.removeEventListener(CREDIT_REFRESH_EVENT, handleCreditRefresh);
    };
  }, [organizationId, refreshCredits]);

  // 402 (クレジット残高不足) を axiosConfig が CustomEvent で通知してくる。
  // 購入できるのはリーダーだけなので、リーダーのときだけ購入モーダルを開く。
  useEffect(() => {
    if (!isLeader) return;
    const handleCreditInsufficient = () => {
      // /home は PC 用ヘッダーと SP 用トップバーに CreditBadge を 1 つずつ描画し
      // CSS (hidden / lg:flex) で出し分けているため、購読者が 2 つになる。両方が
      // モーダルを開くと body の scroll lock が食い違うので、実際に表示されている
      // 側だけを反応させる (display:none 配下なら offsetParent は null)。
      if (dropdownRef.current && dropdownRef.current.offsetParent === null) return;
      setIsDropdownOpen(false);
      setIsPurchaseModalOpen(true);
    };
    window.addEventListener(CREDIT_INSUFFICIENT_EVENT, handleCreditInsufficient);
    return () => window.removeEventListener(CREDIT_INSUFFICIENT_EVENT, handleCreditInsufficient);
  }, [isLeader]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const formatNumber = (n: number) => Math.round(Math.abs(n)).toLocaleString();

  if (loading || !creditData) {
    return null;
  }

  const handleUsageClick = () => {
    setIsDropdownOpen(false);
    setIsModalOpen(true);
  };

  const handlePurchaseClick = () => {
    setIsDropdownOpen(false);
    setIsPurchaseModalOpen(true);
  };

  return (
    <>
      <div className={CONTAINER} ref={dropdownRef}>
        <button className={BADGE} onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
          <SparkleIcon />
          <span>{formatNumber(creditData.total_balance)}</span>
        </button>

        {isDropdownOpen && (
          <div className={DROPDOWN}>
            <div className={DROPDOWN_HEADER}>
              <div className={ORG_NAME}>{creditData.organization_name}</div>
            </div>

            <div className={DROPDOWN_BODY}>
              {/* Credits section */}
              <div className={SECTION}>
                <div className={SECTION_HEADER}>
                  <div className={SECTION_ICON}><SparkleIcon /></div>
                  <span className={SECTION_TITLE}>クレジット</span>
                  <span className={SECTION_VALUE}>{formatNumber(creditData.total_balance)}</span>
                </div>
                <ul className={DETAIL_LIST}>
                  <li className={DETAIL_ITEM}>
                    <span>無料クレジット</span>
                    <span className={DETAIL_VALUE}>{formatNumber(creditData.wallets.free.balance)}</span>
                  </li>
                  <li className={DETAIL_ITEM}>
                    <span>月間クレジット</span>
                    <span className={DETAIL_VALUE}>
                      {formatNumber(creditData.wallets.monthly.balance)} / {formatNumber(creditData.wallets.monthly.limit)}
                    </span>
                  </li>
                  <li className={DETAIL_ITEM}>
                    <span>追加クレジット</span>
                    <span className={DETAIL_VALUE}>{formatNumber(creditData.wallets.additional.balance)}</span>
                  </li>
                  {/* 招待ボーナスは追加クレジットに購入分と混ざって入るため、残高の
                      内訳としては出せない。「これまでに得た累計」として別行にする。 */}
                  {creditData.invite_bonus_total > 0 && (
                    <li className={DETAIL_ITEM}>
                      <span>招待ボーナス（累計）</span>
                      <span className={DETAIL_VALUE}>
                        +{formatNumber(creditData.invite_bonus_total)}
                      </span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Daily refresh section */}
              <div className={SECTION}>
                <div className={SECTION_HEADER}>
                  <div className={SECTION_ICON}><CalendarIcon /></div>
                  <span className={SECTION_TITLE}>毎日更新クレジット</span>
                  <span className={SECTION_VALUE}>{formatNumber(creditData.wallets.free.daily_amount)}</span>
                </div>
                <p className={REFRESH_NOTE}>
                  毎日00:00に{formatNumber(creditData.wallets.free.daily_amount)}へリフレッシュ
                </p>
              </div>
            </div>

            {isLeader && (
              <button className={PURCHASE_LINK} onClick={handlePurchaseClick}>
                <span className={PURCHASE_LINK_CONTENT}>
                  <Plus size={16} />
                  <span>クレジットを追加購入</span>
                </span>
                <ChevronRight size={16} />
              </button>
            )}

            <button className={USAGE_LINK} onClick={handleUsageClick}>
              <span>使用状況を確認</span>
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      <CreditUsageModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        creditData={creditData}
      />

      <CreditPurchaseModal
        isOpen={isPurchaseModalOpen}
        onClose={() => setIsPurchaseModalOpen(false)}
      />
    </>
  );
};

// Sparkle SVG icon
const SparkleIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.09 0.72C14.15 0.54 14.42 0.54 14.48 0.72L14.83 1.77C15.03 2.39 15.51 2.86 16.12 3.07L17.18 3.42C17.36 3.48 17.36 3.74 17.18 3.81L16.12 4.16C15.51 4.36 15.03 4.84 14.83 5.45L14.48 6.5C14.42 6.69 14.15 6.69 14.09 6.5L13.74 5.45C13.54 4.84 13.06 4.36 12.45 4.16L11.4 3.81C11.21 3.74 11.21 3.48 11.4 3.42L12.45 3.07C13.06 2.86 13.54 2.39 13.74 1.77L14.09 0.72Z" fill="currentColor"/>
    <path d="M7.78 2.62C7.93 2.15 8.59 2.15 8.74 2.62L9.68 5.44C10.15 6.85 11.25 7.95 12.66 8.42L15.48 9.36C15.95 9.51 15.95 10.17 15.48 10.33L12.66 11.27C11.25 11.74 10.15 12.84 9.68 14.24L8.74 17.07C8.59 17.53 7.93 17.53 7.78 17.07L6.83 14.24C6.4 12.92 5.4 11.87 4.12 11.36L1.03 10.33C0.57 10.17 0.57 9.51 1.03 9.36L4.12 8.42C5.4 7.98 6.4 6.98 6.83 5.44L7.78 2.62Z" fill="currentColor"/>
  </svg>
);

// Calendar SVG icon
const CalendarIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.96 5.87V5.26H8.02V5.87C8.02 6.29 7.68 6.63 7.26 6.63C6.84 6.63 6.5 6.29 6.5 5.87V5.26H5.22C4.88 5.26 4.61 5.53 4.61 5.87V7.84H15.37V5.87C15.37 5.53 15.09 5.26 14.76 5.26H13.48V5.87C13.48 6.29 13.13 6.63 12.72 6.63C12.3 6.63 11.96 6.29 11.96 5.87ZM4.61 9.35V15.41C4.61 15.75 4.88 16.02 5.22 16.02H9.99L10.07 16.02C10.45 16.06 10.75 16.38 10.75 16.78C10.75 17.17 10.45 17.49 10.07 17.53L9.99 17.53H5.22C4.04 17.53 3.09 16.58 3.09 15.41V5.87C3.09 4.7 4.04 3.75 5.22 3.75H6.5V3.14C6.5 2.72 6.84 2.38 7.26 2.38C7.68 2.38 8.02 2.72 8.02 3.14V3.75H11.96V3.14C11.96 2.72 12.3 2.38 12.72 2.38C13.13 2.38 13.48 2.72 13.48 3.14V3.75H14.76C15.93 3.75 16.88 4.7 16.88 5.87V10.64C16.88 11.06 16.54 11.4 16.12 11.4C15.71 11.4 15.37 11.06 15.37 10.64V9.35H4.61Z" fill="currentColor"/>
    <path d="M13.11 10.65C13.18 10.43 13.49 10.43 13.57 10.65L13.98 11.89C14.22 12.6 14.78 13.17 15.5 13.41L16.74 13.82C16.96 13.89 16.96 14.2 16.74 14.28L15.5 14.69C14.78 14.93 14.22 15.49 13.98 16.21L13.57 17.45C13.49 17.67 13.18 17.67 13.11 17.45L12.7 16.21C12.46 15.49 11.89 14.93 11.17 14.69L9.93 14.28C9.71 14.2 9.71 13.89 9.93 13.82L11.17 13.41C11.89 13.17 12.46 12.6 12.7 11.89L13.11 10.65Z" fill="currentColor"/>
  </svg>
);

export default CreditBadge;
